/**
 * apiV2 — dedicated axios instance for /api/v2/* routes.
 *
 * These endpoints use Supabase Auth JWTs, NOT the consumer app's httpOnly
 * cookie.  Token is stored in localStorage under `medeasy_supa_token` and
 * injected as `Authorization: Bearer <token>` on every request.
 *
 * We keep this separate from lib/api.js so the two auth systems (legacy
 * cookie for patients, Supabase JWT for B2B) never step on each other.
 */

import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const V2 = `${BACKEND_URL}/api/v2`;

export const SUPA_TOKEN_KEY = "medeasy_supa_token";
export const SUPA_USER_KEY = "medeasy_supa_user";

export function getSupaToken() {
  return localStorage.getItem(SUPA_TOKEN_KEY) || null;
}

export function setSupaSession(token, user) {
  if (token) localStorage.setItem(SUPA_TOKEN_KEY, token);
  if (user) localStorage.setItem(SUPA_USER_KEY, JSON.stringify(user));
}

export function clearSupaSession() {
  localStorage.removeItem(SUPA_TOKEN_KEY);
  localStorage.removeItem(SUPA_USER_KEY);
}

export function getSupaUser() {
  try {
    return JSON.parse(localStorage.getItem(SUPA_USER_KEY) || "null");
  } catch {
    return null;
  }
}

export const apiV2 = axios.create({
  baseURL: V2,
  timeout: 15000,
});

apiV2.interceptors.request.use((config) => {
  const token = getSupaToken();
  if (token) config.headers["Authorization"] = `Bearer ${token}`;
  return config;
});

apiV2.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err?.response?.status === 401) {
      clearSupaSession();
      if (!window.location.pathname.startsWith("/b2b/login")) {
        window.location.href = "/b2b/login";
      }
    }
    return Promise.reject(err);
  }
);

/**
 * Download an authenticated CSV.  We can't use a plain <a href> because the
 * endpoint requires an Authorization header.  Fetch with bearer, then blob-URL.
 */
export async function downloadCsvV2(path, filename) {
  const res = await fetch(`${V2}${path}`, {
    headers: { Authorization: `Bearer ${getSupaToken()}` },
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`Download failed (${res.status}): ${text.slice(0, 120)}`);
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
